// src/features/automacaodeemails/pages/AutomacaoHome.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

const ICONS = {
  zap: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="13,2 3,14 12,14 11,22 21,10 12,10 13,2" />
    </svg>
  ),
  monitor: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  ),
  layout: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="21" x2="9" y2="9" />
    </svg>
  ),
  mail: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  history: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12,6 12,12 16,14" />
    </svg>
  ),
  settings: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  assets: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8" cy="8" r="2" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  ),
  plus: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
};

export default function AutomacaoHome() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentAutomations, setRecentAutomations] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);

  const statusLabels = {
    SENT: "Enviado",
    FAILED: "Falha",
    QUEUED: "Na fila",
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  async function loadDashboard() {
    setLoading(true);
    try {
      const [automationsRes, logsStatsRes, logsRes] = await Promise.all([
        api.automations.list({ limit: 3 }),
        api.logs.stats(7),
        api.logs.list({ limit: 3 }),
      ]);

      setRecentAutomations(automationsRes?.data || []);
      setStats(logsStatsRes?.data || null);
      setRecentLogs(logsRes?.data || []);
    } catch (err) {
      console.error("Erro ao carregar dashboard:", err);
    } finally {
      setLoading(false);
    }
  }

  const summaryCards = useMemo(() => {
    if (!stats) return [];
    return [
      {
        label: "Enviados (7 dias)",
        value: stats.general?.sent || 0,
        tone: "success",
        icon: ICONS.mail,
      },
      {
        label: "Falhas (7 dias)",
        value: stats.general?.failed || 0,
        tone: "danger",
        icon: ICONS.history,
      },
      {
        label: "Total de automacoes",
        value: recentAutomations.length,
        tone: "info",
        icon: ICONS.zap,
      },
      {
        label: "Ativas",
        value: recentAutomations.filter((a) => a.status === "active").length,
        tone: "success",
        icon: ICONS.layout,
      },
    ];
  }, [stats, recentAutomations]);

  const heroHighlights = useMemo(() => {
    if (!stats) return [];
    const sent = stats.general?.sent || 0;
    const failed = stats.general?.failed || 0;
    const total = sent + failed;
    const successRate = total > 0 ? Math.round((sent / total) * 100) : 0;
    return [
      { label: "Enviados (7 dias)", value: sent },
      { label: "Falhas (7 dias)", value: failed },
      { label: "Taxa de sucesso", value: `${successRate}%` },
    ];
  }, [stats]);

  if (loading) {
    return (
      <div className="ae2-page">
        <div className="ae2-loading">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="ae2-page ae2-home">
      <section className="ae2-home-hero">
        <div className="ae2-home-hero-content">
          <div className="ae2-home-hero-text">
            <span className="ae2-home-pill">Automação de e-mails</span>
            <h1>Relatórios vivos, enviados no tempo certo.</h1>
            <p>
              Conecte telas, capture dados visuais e deixe o sistema disparar as mensagens com qualidade
              de relatório. Sem planilhas, sem trabalho manual.
            </p>
            <div className="ae2-home-hero-actions">
              <button className="ae2-btn ae2-btn-primary" onClick={() => nav("/automacao-emails/wizard")}>
                {ICONS.plus} Nova automação
              </button>
              <button className="ae2-btn ae2-btn-ghost" onClick={() => nav("/automacao-emails/screens")}>
                {ICONS.plus} Cadastrar tela
              </button>
            </div>
            {heroHighlights.length > 0 && (
              <div className="ae2-home-hero-kpis">
                {heroHighlights.map((item, idx) => (
                  <div key={idx} className="ae2-home-hero-kpi">
                    <div className="ae2-home-hero-kpi-value">{item.value}</div>
                    <div className="ae2-home-hero-kpi-label">{item.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {summaryCards.length > 0 && (
            <div className="ae2-home-hero-panel">
              <div className="ae2-home-panel-title">Visão geral</div>
              <div className="ae2-home-panel-grid">
                {summaryCards.map((card, i) => (
                  <div key={i} className={`ae2-home-panel-card tone-${card.tone}`}>
                    <div className="ae2-home-panel-icon">{card.icon}</div>
                    <div>
                      <div className="ae2-home-panel-value">{card.value}</div>
                      <div className="ae2-home-panel-label">{card.label}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="ae2-home-panel-foot">Atualizado agora • Ultimos 7 dias</div>
            </div>
          )}
        </div>
      </section>

      <section className="ae2-home-section">
        <div className="ae2-home-section-head">
          <div>
            <h3>Atalhos essenciais</h3>
            <p>Construa sua automação em poucos cliques.</p>
          </div>
        </div>
        <div className="ae2-home-action-grid">
          <ActionCard
            icon={ICONS.monitor}
            title="Telas do sistema"
            desc="Cadastre URLs e escaneie automaticamente"
            onClick={() => nav("/automacao-emails/screens")}
          />
          <ActionCard
            icon={ICONS.mail}
            title="Minhas automações"
            desc="Gerencie status, execuções e edições"
            onClick={() => nav("/automacao-emails/automations")}
          />
          <ActionCard
            icon={ICONS.history}
            title="Histórico de envios"
            desc="Acompanhe desempenho e falhas"
            onClick={() => nav("/automacao-emails/logs")}
          />
          <ActionCard
            icon={ICONS.assets}
            title="Assinaturas e imagens"
            desc="Galeria reutilizável para e-mails"
            onClick={() => nav("/automacao-emails/assets")}
          />
          <ActionCard
            icon={ICONS.settings}
            title="Configurações"
            desc="SMTP e parâmetros globais"
            onClick={() => nav("/automacao-emails/settings")}
          />
        </div>
      </section>

      <section className="ae2-home-grid">
        <div className="ae2-home-card">
          <div className="ae2-home-card-header">
            <h3>Automações recentes</h3>
            <button className="ae2-btn ae2-btn-ghost" onClick={() => nav("/automacao-emails/automations")}>
              Ver todas
            </button>
          </div>
          <div className="ae2-list">
            {recentAutomations.length === 0 ? (
              <div className="ae2-empty">
                <p>Nenhuma automação criada ainda.</p>
              </div>
            ) : (
              recentAutomations.map((auto) => (
                <button
                  key={auto.id}
                  type="button"
                  className="ae2-list-row ae2-home-list-row"
                  onClick={() => nav(`/automacao-emails/wizard/${auto.id}`)}
                >
                  <div>
                    <div className="ae2-list-title">{auto.nome}</div>
                    <div className="ae2-list-sub">
                      {auto.freq} as {auto.hora} | {auto.elements_count || 0} elementos
                    </div>
                  </div>
                  <span className={`ae2-badge ${auto.status === "active" ? "success" : auto.status === "paused" ? "warning" : "danger"}`}>
                    {auto.status === "active" ? "Ativo" : auto.status === "paused" ? "Pausado" : "Erro"}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="ae2-home-card">
          <div className="ae2-home-card-header">
            <h3>Envios recentes</h3>
            <button className="ae2-btn ae2-btn-ghost" onClick={() => nav("/automacao-emails/logs")}>
              Ver logs
            </button>
          </div>
          <div className="ae2-list">
            {recentLogs.length === 0 ? (
              <div className="ae2-empty">
                <p>Nenhum envio registrado ainda.</p>
              </div>
            ) : (
              recentLogs.map((log) => (
                <div key={log.id} className="ae2-list-row ae2-list-row-static ae2-home-list-row">
                  <div>
                    <div className="ae2-list-title">{log.automation_nome}</div>
                    <div className="ae2-list-sub">
                      {new Date(log.sent_at).toLocaleString("pt-BR")} | {Array.isArray(log.destinatarios_enviados) ? log.destinatarios_enviados.length : 0} destinatário(s)
                    </div>
                  </div>
                  <span className={`ae2-badge ${log.status === "SENT" ? "success" : log.status === "FAILED" ? "danger" : "warning"}`}>
                    {statusLabels[log.status] || log.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function ActionCard({ icon, title, desc, onClick, tag }) {
  return (
    <button className="ae2-home-action-card" type="button" onClick={onClick}>
      <div className="ae2-home-action-icon">{icon}</div>
      <div className="ae2-home-action-body">
        <div className="ae2-home-action-title">
          {title}
          {tag && <span className="ae2-home-action-tag">{tag}</span>}
        </div>
        <div className="ae2-home-action-desc">{desc}</div>
      </div>
    </button>
  );
}
